/**
 * QBridge Service Layer
 * Handles communication with QBridge local desktop application
 *
 * QBridge runs locally on user's machine at localhost:5556
 * and bridges MT5 trading data to Q AI backend
 *
 * v1.9.0+ Features:
 * - /install-info endpoint for comprehensive status
 * - qbridge:// URL protocol for auto-starting QBridge
 * - Smart detection flow with auto-start capability
 */

// QBridge local server URL
const QBRIDGE_LOCAL_URL = 'http://localhost:5556';

// Polling intervals
export const POLLING_INTERVALS = {
    QBRIDGE_STATUS: 2000,   // 2 seconds
    MT5_DETECTION: 3000,    // 3 seconds
    IMPORT_PROGRESS: 1000,  // 1 second
    STARTUP_POLL: 1000      // 1 second (for waiting QBridge to start)
};

// QBridge status states
export const QBRIDGE_STATUS = {
    UNKNOWN: 'unknown',
    NOT_RUNNING: 'not-running',
    READY: 'ready',
    ERROR: 'error',
    STARTING: 'starting'    // New: QBridge is being started via protocol
};

// MT5 detection states
export const MT5_STATUS = {
    NOT_DETECTED: 'not-detected',
    SEARCHING: 'searching',
    DETECTED: 'detected',
    ERROR: 'error'
};

// Auth states
export const AUTH_STATUS = {
    PENDING: 'pending',
    AUTHORIZED: 'authorized',
    FAILED: 'failed'
};

// Import states
export const IMPORT_STATUS = {
    IDLE: 'idle',
    IMPORTING: 'importing',
    SUCCESS: 'success',
    ERROR: 'error'
};

/**
 * Check if QBridge desktop app is running (legacy endpoint)
 * @returns {Promise<{status: string, version: string | null, error: string | null}>}
 */
export const checkQBridgeStatus = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/status`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return {
                status: QBRIDGE_STATUS.ERROR,
                version: null,
                error: `Status check failed: ${response.status}`
            };
        }

        const data = await response.json();
        return {
            status: data.status === 'ready' ? QBRIDGE_STATUS.READY : QBRIDGE_STATUS.NOT_RUNNING,
            version: data.version || null,
            error: null
        };
    } catch (error) {
        // Network error means QBridge is not running
        return {
            status: QBRIDGE_STATUS.NOT_RUNNING,
            version: null,
            error: error.message
        };
    }
};

/**
 * Get comprehensive installation info from QBridge (v1.9.0+)
 * Single endpoint that returns QBridge, MT5, auth, and chartBridge status
 * @param {number} timeout - Request timeout in ms (default 3000)
 * @returns {Promise<{
 *   success: boolean,
 *   qbridge: { running: boolean, version: string, port: number } | null,
 *   mt5: { detected: boolean, connected: boolean, account: string, broker: string, balance: number, currency: string } | null,
 *   auth: { authorized: boolean, email: string, balance: number } | null,
 *   chartBridge: { running: boolean, files_path: string } | null,
 *   ready: boolean,
 *   needsAuth: boolean,
 *   needsMt5: boolean,
 *   error: string | null
 * }>}
 */
export const getInstallInfo = async (timeout = 3000) => {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(`${QBRIDGE_LOCAL_URL}/install-info`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            return {
                success: false,
                qbridge: null,
                mt5: null,
                auth: null,
                chartBridge: null,
                ready: false,
                needsAuth: true,
                needsMt5: true,
                error: `Request failed: ${response.status}`
            };
        }

        const data = await response.json();
        return {
            success: true,
            qbridge: data.qbridge || { running: true, version: 'unknown', port: 5556 },
            mt5: data.mt5 || null,
            auth: data.auth || null,
            chartBridge: data.chartBridge || null,
            ready: data.ready || false,
            needsAuth: data.needsAuth !== false,
            needsMt5: data.needsMt5 !== false,
            error: null
        };
    } catch (error) {
        // Network error means QBridge is not running
        return {
            success: false,
            qbridge: null,
            mt5: null,
            auth: null,
            chartBridge: null,
            ready: false,
            needsAuth: true,
            needsMt5: true,
            error: error.name === 'AbortError' ? 'Connection timeout' : error.message
        };
    }
};

/**
 * Try to start QBridge via the qbridge:// URL protocol (v1.9.0+)
 * Uses a hidden iframe to trigger the protocol handler
 * @returns {Promise<boolean>} - true if QBridge started successfully
 */
export const tryStartQBridge = async () => {
    return new Promise((resolve) => {
        // Create hidden iframe to trigger protocol
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.style.width = '0';
        iframe.style.height = '0';
        document.body.appendChild(iframe);

        // Try to trigger qbridge:// protocol
        try {
            iframe.src = 'qbridge://start';
        } catch (e) {
            // Protocol not registered
            document.body.removeChild(iframe);
            resolve(false);
            return;
        }

        // Poll for QBridge to come online
        let attempts = 0;
        const maxAttempts = 6; // 6 seconds total

        const pollForQBridge = async () => {
            attempts++;

            // Check if QBridge is now running
            const info = await getInstallInfo(2000);
            if (info.success && info.qbridge?.running) {
                // QBridge started!
                try {
                    document.body.removeChild(iframe);
                } catch (e) { /* iframe may already be removed */ }
                resolve(true);
                return;
            }

            if (attempts < maxAttempts) {
                // Keep polling
                setTimeout(pollForQBridge, POLLING_INTERVALS.STARTUP_POLL);
            } else {
                // Timeout - QBridge didn't start
                try {
                    document.body.removeChild(iframe);
                } catch (e) { /* iframe may already be removed */ }
                resolve(false);
            }
        };

        // Start polling after brief delay to allow protocol to trigger
        setTimeout(pollForQBridge, 1000);
    });
};

/**
 * Smart QBridge connection flow for sign-in (v1.9.0+)
 * 1. Check if QBridge is running via /install-info
 * 2. If not, try to start via qbridge:// protocol
 * 3. Return comprehensive status
 * @param {string} jwtToken - JWT token to authorize with QBridge
 * @returns {Promise<{
 *   connected: boolean,
 *   autoStarted: boolean,
 *   installInfo: object | null,
 *   authResult: object | null,
 *   error: string | null,
 *   needsManualStart: boolean,
 *   needsDownload: boolean
 * }>}
 */
export const smartConnectQBridge = async (jwtToken) => {
    // Step 1: Check if QBridge is already running
    let installInfo = await getInstallInfo();

    if (installInfo.success && installInfo.qbridge?.running) {
        // QBridge is running - authorize and return
        const authResult = await authorizeQBridge(jwtToken);
        return {
            connected: authResult.success,
            autoStarted: false,
            installInfo,
            authResult,
            error: authResult.success ? null : authResult.error,
            needsManualStart: false,
            needsDownload: false
        };
    }

    // Step 2: QBridge not running - try to start via protocol
    const started = await tryStartQBridge();

    if (started) {
        // QBridge started! Get updated info and authorize
        installInfo = await getInstallInfo();
        const authResult = await authorizeQBridge(jwtToken);
        return {
            connected: authResult.success,
            autoStarted: true,
            installInfo,
            authResult,
            error: authResult.success ? null : authResult.error,
            needsManualStart: false,
            needsDownload: false
        };
    }

    // Step 3: Couldn't start - QBridge not installed or protocol not registered
    return {
        connected: false,
        autoStarted: false,
        installInfo: null,
        authResult: null,
        error: 'QBridge not detected',
        needsManualStart: true,
        needsDownload: true
    };
};

/**
 * Detect MT5 terminal via QBridge
 * Primary: Uses /api/mt5/detect endpoint
 * Fallback: If 404, uses /api/ea/list to infer MT5 connection (if files exist, MT5 folder is accessible)
 * @returns {Promise<{detected: boolean, account: string | null, broker: string | null, balance: number | null, currency: string | null, connected: boolean, error: string | null}>}
 */
export const detectMT5 = async () => {
    try {
        // Primary: Try dedicated MT5 detection endpoint
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/mt5/detect`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
            const data = await response.json();
            // QBridge returns: { detected, account, broker, balance, currency, connected }
            return {
                detected: data.detected || false,
                account: data.account || null,
                broker: data.broker || null,
                balance: data.balance || null,
                currency: data.currency || null,
                connected: data.connected || data.detected || false,
                error: null
            };
        }

        // Fallback: If /api/mt5/detect returns 404, try EA list to infer MT5 connection
        // If QBridge can list EA files, MT5 folder must be accessible
        if (response.status === 404) {
            console.log('[detectMT5] /api/mt5/detect not available, using fallback via /api/ea/list');
            return await detectMT5Fallback();
        }

        return {
            detected: false,
            account: null,
            broker: null,
            balance: null,
            currency: null,
            connected: false,
            error: `MT5 detection failed: ${response.status}`
        };
    } catch (error) {
        // Network error - try fallback
        console.log('[detectMT5] Primary detection failed, trying fallback:', error.message);
        return await detectMT5Fallback();
    }
};

/**
 * Fallback MT5 detection using EA file list
 * If QBridge can list EA files, it means MT5 folder is accessible
 * @returns {Promise<{detected: boolean, account: string | null, broker: string | null, balance: number | null, currency: string | null, connected: boolean, error: string | null}>}
 */
const detectMT5Fallback = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/ea/list`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return {
                detected: false,
                account: null,
                broker: null,
                balance: null,
                currency: null,
                connected: false,
                error: 'MT5 not detected (EA list unavailable)'
            };
        }

        const data = await response.json();

        // If we have files, MT5 folder is accessible
        // This is a "soft" detection - MT5 files exist so QBridge can access them
        if (data.success && data.files && data.files.length > 0) {
            return {
                detected: true,
                account: null,  // Account info not available via fallback
                broker: null,
                balance: null,
                currency: null,
                connected: true,
                error: null,
                _fallback: true  // Flag to indicate this is fallback detection
            };
        }

        return {
            detected: false,
            account: null,
            broker: null,
            balance: null,
            currency: null,
            connected: false,
            error: 'No MT5 files found'
        };
    } catch (error) {
        return {
            detected: false,
            account: null,
            broker: null,
            balance: null,
            currency: null,
            connected: false,
            error: error.message
        };
    }
};

/**
 * Authorize QBridge with JWT token
 * @param {string} token - JWT token from Q AI authentication
 * @returns {Promise<{success: boolean, error: string | null}>}
 */
export const authorizeQBridge = async (token) => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/auth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.error || `Authorization failed: ${response.status}`
            };
        }

        const data = await response.json();
        return {
            success: data.success || true,
            error: null
        };
    } catch (error) {
        return {
            success: false,
            error: error.message || 'Could not connect to QBridge'
        };
    }
};

/**
 * Check if QBridge already has valid authorization
 * @returns {Promise<{authorized: boolean, email: string | null}>}
 */
export const checkQBridgeAuth = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/auth/status`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return { authorized: false, email: null };
        }

        const data = await response.json();
        return {
            authorized: data.authorized || false,
            email: data.email || null
        };
    } catch {
        return { authorized: false, email: null };
    }
};

/**
 * Trigger trade import via QBridge
 * @returns {Promise<{success: boolean, imported: number, total: number, error: string | null}>}
 */
export const importTrades = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                imported: 0,
                total: 0,
                error: errorData.error || `Import failed: ${response.status}`
            };
        }

        const data = await response.json();
        return {
            success: data.success || true,
            imported: data.imported || 0,
            total: data.total || data.imported || 0,
            message: data.message || null,
            error: null
        };
    } catch (error) {
        return {
            success: false,
            imported: 0,
            total: 0,
            error: error.message || 'Import failed'
        };
    }
};

/**
 * Get import progress (if QBridge supports streaming updates)
 * @returns {Promise<{inProgress: boolean, progress: number, imported: number, total: number}>}
 */
export const getImportProgress = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/import/progress`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return { inProgress: false, progress: 0, imported: 0, total: 0 };
        }

        const data = await response.json();
        return {
            inProgress: data.inProgress || false,
            progress: data.progress || 0,
            imported: data.imported || 0,
            total: data.total || 0
        };
    } catch {
        return { inProgress: false, progress: 0, imported: 0, total: 0 };
    }
};

/**
 * Get trade preview before import
 * @returns {Promise<{success: boolean, tradeCount: number, dateRange: {from: string, to: string}, summary: object}>}
 */
export const getTradePreview = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/trades/preview`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            return {
                success: false,
                tradeCount: 0,
                dateRange: null,
                summary: null
            };
        }

        const data = await response.json();
        return {
            success: true,
            tradeCount: data.tradeCount || 0,
            dateRange: data.dateRange || null,
            summary: data.summary || null
        };
    } catch {
        return {
            success: false,
            tradeCount: 0,
            dateRange: null,
            summary: null
        };
    }
};

/**
 * Known broker logo mapping
 */
export const BROKER_LOGOS = {
    'ICMarkets': '/images/brokers/icmarkets.png',
    'IC Markets': '/images/brokers/icmarkets.png',
    'Pepperstone': '/images/brokers/pepperstone.png',
    'OANDA': '/images/brokers/oanda.png',
    'FXCM': '/images/brokers/fxcm.png',
    'XM': '/images/brokers/xm.png',
    'Exness': '/images/brokers/exness.png',
    'FBS': '/images/brokers/fbs.png',
    'HotForex': '/images/brokers/hotforex.png',
    'Alpari': '/images/brokers/alpari.png',
    'default': '/images/brokers/default.png'
};

/**
 * Get broker logo URL
 * @param {string} brokerName
 * @returns {string}
 */
export const getBrokerLogo = (brokerName) => {
    if (!brokerName) return BROKER_LOGOS.default;

    // Try exact match first
    if (BROKER_LOGOS[brokerName]) {
        return BROKER_LOGOS[brokerName];
    }

    // Try partial match
    const normalizedName = brokerName.toLowerCase();
    for (const [key, value] of Object.entries(BROKER_LOGOS)) {
        if (normalizedName.includes(key.toLowerCase())) {
            return value;
        }
    }

    return BROKER_LOGOS.default;
};

/**
 * Get comprehensive QBridge status (alias for getInstallInfo)
 * Used by QAIMT5ReadyPage for status display
 * @returns {Promise<object>}
 */
export const getQBridgeStatus = getInstallInfo;

// ═══════════════════════════════════════════════════════════════════════════
// QBRIDGE PROTOCOL HANDLERS
// Used for deep-linking actions from MT5 Panel to web (payment, settings, etc.)
// Protocol format: qforgex://action/params
// ═══════════════════════════════════════════════════════════════════════════

/**
 * QBridge Protocol Actions
 * These are triggered when MT5 Panel sends a protocol request to open web pages
 */
export const QBRIDGE_PROTOCOL_ACTIONS = {
    BUY_TOKENS: 'buy-tokens',          // Open token purchase page
    ACCOUNT_SETTINGS: 'account',        // Open account settings
    SUBSCRIPTION: 'subscription',       // Open subscription management
    SUPPORT: 'support'                  // Open support page
};

/**
 * Build web URL for QBridge protocol action
 * QBridge will call this URL pattern when user clicks "Buy Tokens" in MT5 Panel
 *
 * @param {string} action - Action type (buy-tokens, account, subscription, support)
 * @param {object} params - URL parameters to include
 * @returns {string} Full web URL
 *
 * @example
 * buildProtocolUrl('buy-tokens', { source: 'mt5-panel', mt5Account: '12345' })
 * // Returns: https://qforgex.com/q-ai/tokens?source=mt5-panel&mt5Account=12345
 */
export const buildProtocolUrl = (action, params = {}) => {
    const baseUrl = window.location.origin;

    const actionPaths = {
        [QBRIDGE_PROTOCOL_ACTIONS.BUY_TOKENS]: '/q-ai/tokens',
        [QBRIDGE_PROTOCOL_ACTIONS.ACCOUNT_SETTINGS]: '/q-ai/account',
        [QBRIDGE_PROTOCOL_ACTIONS.SUBSCRIPTION]: '/q-ai/subscription',
        [QBRIDGE_PROTOCOL_ACTIONS.SUPPORT]: '/q-ai/support'
    };

    const path = actionPaths[action] || '/q-ai/coach';
    const queryString = new URLSearchParams(params).toString();

    return queryString ? `${baseUrl}${path}?${queryString}` : `${baseUrl}${path}`;
};

/**
 * Parse QBridge protocol URL into action and params
 * Used when web page loads from qforgex:// protocol redirect
 *
 * @param {string} url - URL to parse (can be full URL or just search params)
 * @returns {{ action: string, params: object }}
 */
export const parseProtocolUrl = (url) => {
    try {
        const urlObj = new URL(url, window.location.origin);
        const params = Object.fromEntries(urlObj.searchParams.entries());

        // Determine action from path
        const path = urlObj.pathname;
        let action = null;

        if (path.includes('/tokens')) action = QBRIDGE_PROTOCOL_ACTIONS.BUY_TOKENS;
        else if (path.includes('/account')) action = QBRIDGE_PROTOCOL_ACTIONS.ACCOUNT_SETTINGS;
        else if (path.includes('/subscription')) action = QBRIDGE_PROTOCOL_ACTIONS.SUBSCRIPTION;
        else if (path.includes('/support')) action = QBRIDGE_PROTOCOL_ACTIONS.SUPPORT;

        return { action, params };
    } catch (error) {
        return { action: null, params: {} };
    }
};

/**
 * Notify QBridge of payment completion
 * Called after successful token purchase to update MT5 Panel balance
 *
 * @param {object} paymentResult - Payment result details
 * @param {number} paymentResult.tokensAdded - Number of tokens purchased
 * @param {number} paymentResult.newBalance - New total token balance
 * @param {string} paymentResult.transactionId - Payment transaction ID
 * @returns {Promise<{ success: boolean, error: string | null }>}
 */
export const notifyPaymentComplete = async (paymentResult) => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/payment/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tokensAdded: paymentResult.tokensAdded,
                newBalance: paymentResult.newBalance,
                transactionId: paymentResult.transactionId,
                timestamp: new Date().toISOString()
            })
        });

        if (!response.ok) {
            // QBridge may not have this endpoint yet - graceful degradation
            if (response.status === 404) {
                console.warn('[QBridge] Payment notification endpoint not available');
                return { success: true, error: null }; // Silent success
            }
            return { success: false, error: `Notification failed: ${response.status}` };
        }

        const data = await response.json();
        return { success: data.success || true, error: null };
    } catch (error) {
        // QBridge may not be running - that's OK, web handles balance
        console.warn('[QBridge] Could not notify payment completion:', error.message);
        return { success: true, error: null }; // Silent success
    }
};

/**
 * Request QBridge to refresh token balance from backend
 * Used after web purchases to sync MT5 Panel display
 *
 * @returns {Promise<{ success: boolean, balance: number | null, error: string | null }>}
 */
export const requestBalanceRefresh = async () => {
    try {
        const response = await fetch(`${QBRIDGE_LOCAL_URL}/api/auth/refresh-balance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: true, balance: null, error: null };
            }
            return { success: false, balance: null, error: `Refresh failed: ${response.status}` };
        }

        const data = await response.json();
        return {
            success: data.success || true,
            balance: data.balance || null,
            error: null
        };
    } catch (error) {
        console.warn('[QBridge] Could not request balance refresh:', error.message);
        return { success: true, balance: null, error: null };
    }
};

const qbridgeService = {
    checkQBridgeStatus,
    getInstallInfo,
    getQBridgeStatus,
    tryStartQBridge,
    smartConnectQBridge,
    detectMT5,
    authorizeQBridge,
    checkQBridgeAuth,
    importTrades,
    getImportProgress,
    getTradePreview,
    getBrokerLogo,
    // Protocol handlers
    buildProtocolUrl,
    parseProtocolUrl,
    notifyPaymentComplete,
    requestBalanceRefresh,
    QBRIDGE_STATUS,
    MT5_STATUS,
    AUTH_STATUS,
    IMPORT_STATUS,
    POLLING_INTERVALS,
    QBRIDGE_PROTOCOL_ACTIONS
};

export default qbridgeService;
